/**
 * Food ("Alimentación") configuration for Cotiza v2: wedding-day food moments
 * and the slug-based mapping of menu items to display groups. Groups are NOT
 * inferred from the category tree; any active item no rule matches lands in
 * the `mas` group so new admin categories still show up.
 */
export const FOOD_MOMENT_IDS = [
    'coctel',
    'cena',
    'trasnocho',
    'bebidas',
    'desayuno',
    'pruebas',
];
export const FOOD_GROUP_KEYS = [
    'mains',
    'infantil',
    'entrada',
    'carta',
    'pasabocas',
    'trasnocho',
    'bebidas',
    'desayuno',
    'pruebas',
    'team',
    'hidden',
    'mas',
];
export const FOOD_SECTION_LABELS = {
    dia: 'Durante el día',
    adicionales: 'Adicionales',
};
export const FOOD_MOMENTS = [
    {
        id: 'cena',
        name: 'Cena',
        icon: 'Utensils',
        section: 'dia',
        timelineMoment: 'cena',
        emptyCopy: 'Elige el plato fuerte de la cena',
        groups: ['mains', 'entrada', 'infantil', 'carta'],
        hasMains: true,
    },
    {
        id: 'coctel',
        name: 'Cóctel',
        icon: 'Wine',
        section: 'dia',
        timelineMoment: 'coctel',
        emptyCopy: 'Pasabocas de bienvenida',
        groups: ['pasabocas'],
    },
    {
        id: 'trasnocho',
        name: 'Trasnocho',
        icon: 'Moon',
        section: 'dia',
        timelineMoment: 'trasnocho',
        emptyCopy: 'Algo caliente para el cierre de la fiesta',
        groups: ['trasnocho'],
    },
    {
        id: 'bebidas',
        name: 'Bebidas y café',
        icon: 'Coffee',
        section: 'dia',
        timeLabel: 'Toda la noche',
        emptyCopy: 'Jugos o estación de café',
        groups: ['bebidas'],
    },
    {
        id: 'desayuno',
        name: 'Desayuno',
        icon: 'Sunrise',
        section: 'adicionales',
        timeLabel: 'Día siguiente',
        emptyCopy: 'Para quienes se hospedan',
        groups: ['desayuno'],
    },
    {
        id: 'pruebas',
        name: 'Prueba de menú',
        icon: 'ChefHat',
        section: 'adicionales',
        timeLabel: 'Antes de la boda',
        emptyCopy: 'Prueba los platos antes del gran día',
        groups: ['pruebas'],
    },
];
export const FOOD_HIGH_PRICE_THRESHOLD = 150000;
const MG = 'momentos_gastronomicos';
const EQ = 'equipo_de_cocina_servicio_y_apoyo';
const MENU_A_TU_MEDIDA = 'menu_a_tu_medida';
export const FOOD_GROUPS = {
    mains: {
        key: 'mains',
        label: 'Plato fuerte',
        hint: 'El menú principal de la cena',
        addLabel: 'Agregar',
        moment: 'cena',
        rules: [
            {
                category: 'combo',
                subcategories: ['sugerido', 'criollo', 'descomplicado'],
                menuType: 'preset_combo',
            },
        ],
        defaultQuantity: 'guests',
    },
    infantil: {
        key: 'infantil',
        label: 'Menú infantil',
        hint: 'Para los niños',
        addLabel: 'Agregar menú infantil',
        moment: 'cena',
        rules: [{ category: 'combo', subcategories: ['infantil'], menuType: 'preset_combo' }],
        defaultQuantity: 'one',
    },
    entrada: {
        key: 'entrada',
        label: 'Entrada',
        hint: 'Antes del plato fuerte',
        addLabel: 'Agregar entrada',
        moment: 'cena',
        rules: [
            {
                category: 'appetizer',
                subcategories: ['ceviches', 'entradas_calientes', 'gratinados', 'sopas_y_cremas'],
            },
            // Prod's tree: menu_a_tu_medida > menu_a_tu_medida_entradas > …_ceviches, …
            { category: MENU_A_TU_MEDIDA, subcategories: [`${MENU_A_TU_MEDIDA}_entradas`] },
        ],
        defaultQuantity: 'guests',
    },
    carta: {
        key: 'carta',
        label: 'Platos a la carta y acompañamientos',
        hint: 'Para armar tu propio plato',
        addLabel: 'Armar un plato a la carta',
        moment: 'cena',
        rules: [
            { category: 'platos_fuertes', subcategoryPrefix: 'platos_fuertes_' },
            { category: 'side', subcategories: ['harinas', 'verduras_calientes', 'verduras_frias'] },
            {
                category: MENU_A_TU_MEDIDA,
                subcategories: [`${MENU_A_TU_MEDIDA}_platos_fuertes`, `${MENU_A_TU_MEDIDA}_acompanamientos`],
            },
        ],
        defaultQuantity: 'guests',
    },
    pasabocas: {
        key: 'pasabocas',
        label: 'Pasabocas de bienvenida',
        hint: 'Durante el cóctel',
        addLabel: 'Agregar',
        moment: 'coctel',
        rules: [
            {
                category: MG,
                subcategories: [`${MG}_pasabocas_de_bienvenida_o_coctel`],
                menuType: 'complement',
            },
        ],
        defaultQuantity: 'guests',
    },
    trasnocho: {
        key: 'trasnocho',
        label: 'Trasnocho',
        hint: 'Para cerrar la fiesta',
        addLabel: 'Agregar',
        moment: 'trasnocho',
        rules: [
            { category: MG, subcategories: [`${MG}_trasnochos_de_fiesta`], menuType: 'complement' },
        ],
        defaultQuantity: 'guests',
    },
    bebidas: {
        key: 'bebidas',
        label: 'Bebidas y café',
        hint: 'Toda la noche',
        addLabel: 'Agregar',
        moment: 'bebidas',
        rules: [
            {
                category: MG,
                subcategories: [`${MG}_bebidas_naturales`, `${MG}_estacion_de_cafe_y_aromatica`],
                menuType: 'complement',
            },
        ],
        defaultQuantity: 'guests',
    },
    desayuno: {
        key: 'desayuno',
        label: 'Desayuno del día siguiente',
        hint: 'Para los huéspedes',
        addLabel: 'Agregar',
        moment: 'desayuno',
        rules: [
            { category: MG, subcategories: [`${MG}_desayunos_del_dia_siguiente`], menuType: 'breakfast' },
        ],
        defaultQuantity: 'per_guest',
    },
    pruebas: {
        key: 'pruebas',
        label: 'Prueba de menú',
        hint: 'Antes de la boda',
        addLabel: 'Agregar',
        moment: 'pruebas',
        rules: [
            { category: EQ, subcategories: [`${EQ}_pruebas_de_menu`], menuType: 'complement' },
        ],
        defaultQuantity: 'per_guest',
    },
    team: {
        key: 'team',
        label: 'Equipo de cocina',
        hint: 'Incluido en tu evento',
        addLabel: 'Agregar',
        moment: null,
        rules: [
            { category: EQ, subcategories: [`${EQ}_equipo_gastronomico`], menuType: 'complement' },
        ],
        defaultQuantity: 'one',
    },
    hidden: {
        key: 'hidden',
        label: 'Alimentación de personal',
        hint: '',
        addLabel: 'Agregar',
        moment: null,
        rules: [
            { category: EQ, subcategories: [`${EQ}_alimentacion_de_personal_y_proveedores`] },
            { menuType: 'staff_menu' },
        ],
        defaultQuantity: 'one',
    },
    mas: {
        key: 'mas',
        label: 'Más opciones',
        hint: '',
        addLabel: 'Agregar',
        moment: null,
        rules: [],
        defaultQuantity: 'guests',
    },
};
/** `team` and `hidden` are never client-pickable. */
export function isFoodGroupPickable(key) {
    return key !== 'team' && key !== 'hidden';
}
/** Evaluation order: staff first (never shown), then the pickable groups. */
const MATCH_ORDER = [
    'hidden',
    ...FOOD_GROUP_KEYS.filter((k) => k !== 'hidden' && k !== 'mas'),
];
function slugOf(ref, lookup) {
    if (!ref)
        return undefined;
    if (typeof ref === 'object')
        return ref.slug;
    if (!lookup)
        return undefined;
    return lookup instanceof Map
        ? lookup.get(ref)
        : lookup[ref];
}
function ruleMatches(rule, category, subs, menuType) {
    if (rule.menuType && rule.menuType !== menuType)
        return false;
    if (rule.category && rule.category !== category)
        return false;
    const needsSub = rule.subcategories || rule.subcategoryPrefix;
    if (!needsSub)
        return true;
    return subs.some((s) => rule.subcategories?.includes(s) ||
        (rule.subcategoryPrefix !== undefined && s.startsWith(rule.subcategoryPrefix)));
}
/**
 * Resolves the display group of a menu item. Category refs may be populated
 * docs or ids (pass `slugById` for ids). First matching rule wins; unmatched
 * active items go to `mas`, unmatched inactive items to `null`.
 */
export function foodGroupOf(item, slugById) {
    const category = slugOf(item.category_id, slugById);
    const subs = [
        slugOf(item.subsubcategory_id, slugById),
        slugOf(item.subcategory_id, slugById),
    ].filter((s) => !!s);
    for (const key of MATCH_ORDER) {
        if (FOOD_GROUPS[key].rules.some((r) => ruleMatches(r, category, subs, item.menu_type))) {
            return key;
        }
    }
    return item.is_active === false ? null : 'mas';
}
/** Default quantity when the client adds an item (mirrors prototype `foodDefaultQ`). */
export function foodDefaultQuantity(item, group, guestCount) {
    const rule = FOOD_GROUPS[group].defaultQuantity;
    if (rule === 'one')
        return 1;
    if (item.quantity_calculation?.type === 'per_guest')
        return guestCount;
    if (rule === 'per_guest')
        return 1;
    return (item.price_per_person ?? 0) > FOOD_HIGH_PRICE_THRESHOLD ? 1 : guestCount;
}
